/**
 * WCAG 2.x relative luminance and contrast for theme-colored headings.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * https://www.w3.org/TR/WCAG21/#contrast-minimum
 */

export type Rgb = { r: number; g: number; b: number };

/** AA minimum for large text: 18pt regular, or 14pt bold and larger. */
export const WCAG_AA_LARGE_TEXT = 3;

/** AA minimum for normal text, including body-size links in blurbs. */
export const WCAG_AA_NORMAL_TEXT = 4.5;

const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const WHITE: Rgb = { r: 255, g: 255, b: 255 };

function clampChannel(value: number): number {
  return Math.min(255, Math.max(0, value));
}

/** Parses #RGB, #RRGGBB, rgb(), rgba(), and the keywords black/white. */
export function parseColor(input: string): Rgb | null {
  const value = input.trim().toLowerCase();
  if (value === 'black') return BLACK;
  if (value === 'white') return WHITE;

  const hex = value.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/);
  if (hex) {
    const raw = hex[1].length <= 4 ? hex[1].slice(0, 3) : hex[1].slice(0, 6);
    const expanded = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    return {
      r: parseInt(expanded.slice(0, 2), 16),
      g: parseInt(expanded.slice(2, 4), 16),
      b: parseInt(expanded.slice(4, 6), 16),
    };
  }

  const rgb = value.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+%?\s*)?\)$/,
  );
  if (rgb) {
    return {
      r: clampChannel(Number(rgb[1])),
      g: clampChannel(Number(rgb[2])),
      b: clampChannel(Number(rgb[3])),
    };
  }

  return null;
}

function linearize(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: Rgb): number {
  return 0.2126 * linearize(color.r) + 0.7152 * linearize(color.g) + 0.0722 * linearize(color.b);
}

export function contrastRatio(foreground: Rgb, background: Rgb): number {
  const lighter = Math.max(relativeLuminance(foreground), relativeLuminance(background));
  const darker = Math.min(relativeLuminance(foreground), relativeLuminance(background));
  return (lighter + 0.05) / (darker + 0.05);
}

/** sRGB mix matching `color-mix(in srgb, color percentage, other)`. */
export function mixSrgb(color: Rgb, other: Rgb, colorWeight: number): Rgb {
  const weight = Math.min(1, Math.max(0, colorWeight));
  return {
    r: color.r * weight + other.r * (1 - weight),
    g: color.g * weight + other.g * (1 - weight),
    b: color.b * weight + other.b * (1 - weight),
  };
}

function solidContrastColor(background: Rgb): '#000000' | '#ffffff' {
  const onBlack = contrastRatio(BLACK, background);
  const onWhite = contrastRatio(WHITE, background);
  return onBlack >= onWhite ? '#000000' : '#ffffff';
}

/**
 * Solid text color when a flat theme color fails WCAG AA against the page
 * background. Returns null when the theme color already passes.
 */
export function contrastSafeTextColor(
  foreground: string,
  background: string,
  minRatio: number = WCAG_AA_NORMAL_TEXT,
): '#000000' | '#ffffff' | null {
  const ink = parseColor(foreground);
  const surface = parseColor(background);
  if (!ink || !surface) return null;
  if (contrastRatio(ink, surface) >= minRatio) return null;
  return solidContrastColor(surface);
}

/**
 * Solid heading color when a theme-color gradient fails WCAG AA against the
 * page background. The overview title paints primary → 80% primary / 20% black;
 * both stops are checked. Returns null when the gradient already passes, so
 * the theme color can stay.
 */
export function contrastSafeHeadingColor(
  primary: string,
  background: string,
  minRatio: number = WCAG_AA_LARGE_TEXT,
): '#000000' | '#ffffff' | null {
  const foreground = parseColor(primary);
  const surface = parseColor(background);
  if (!foreground || !surface) return null;

  const darkened = mixSrgb(foreground, BLACK, 0.8);
  const worst = Math.min(contrastRatio(foreground, surface), contrastRatio(darkened, surface));
  if (worst >= minRatio) return null;

  return solidContrastColor(surface);
}
